/**
 * M3X.WORKSPACE navigation registry (single source of truth).
 *
 * Per BUILD_READINESS_M3X_WORKSPACE_FULL.md §2.2 + §4 inventory.
 *
 * F.1-F.4 — registry shape.
 * F.15-F.22 — full wiring sweep: every shipped + Wire (B) orphan route
 *             registered with EN+AR labels. Includes the 24 original
 *             orphans + the 7 COMP orphans (M3X.COMP shipped on legacy
 *             chassis 2026-05-24, retroactively wired here).
 * F.23-F.30 — NEW (C) second-line screens add their entries as each
 *             phase builds them.
 *
 * Every new route MUST register here. Lint
 * `test_every_route_in_registry` is merge-blocking.
 */

import type { LucideIcon } from "lucide-react";
import {
  Activity,
  Banknote,
  Bot,
  Briefcase,
  Building2,
  CalendarDays,
  ChartLine,
  ClipboardCheck,
  ClipboardList,
  CreditCard,
  HeartPulse,
  KeySquare,
  LayoutDashboard,
  Library,
  LogOut,
  PieChart,
  Plane,
  Settings as SettingsIcon,
  ShieldCheck,
  Sparkles,
  TrendingUp,
  UserCog,
  Users,
} from "lucide-react";

import {
  type AttentionCountContext,
  attentionCounts,
} from "../lib/attentionCounts";


export type ModuleId =
  | "mission-control"
  | "people"
  | "operations"
  | "policies"
  | "ai"
  | "reports"
  | "settings"
  | "me";


export interface Screen {
  id: string;
  route: string;
  labelEn: string;
  labelAr: string;
  attention?: boolean;
  countSource?: (ctx: AttentionCountContext) => Promise<number>;
  briefAgent?: "tashghil" | "mutabaa" | "rawatib" | "khidmat";
  /** Status: 'shipped' / 'wire-orphan' / 'new-pending' for the audit. */
  status?: "shipped" | "wire-orphan" | "new-pending";
  /**
   * Session 3e PR 3 — explicit menu-discoverability bypass.
   * When true, the route is registered for the orphan-lint to pass
   * but is NOT surfaced in any menu walk. Required for system routes
   * (auth flows · Clerk callbacks · error pages), detail pages
   * reached via parent-list row clicks, Navigate redirects from
   * legacy paths, module landings reachable via Rail 1 module
   * clicks, and onboarding-step pages reachable from the
   * Settings → Setup journey.
   * MUST be paired with a non-empty `reason` field.
   */
  internalOnly?: boolean;
  /** Required when internalOnly=true. Documents why the route bypasses the menu. */
  reason?: string;
}


export interface SubDomain {
  id: string;
  module: ModuleId;
  icon: LucideIcon;
  labelEn: string;
  labelAr: string;
  attention?: boolean;
  countSource?: (ctx: AttentionCountContext) => Promise<number>;
  defaultBriefAgent?: "tashghil" | "mutabaa" | "rawatib" | "khidmat";
  screens: Screen[];
}


export const NAVIGATION_REGISTRY: SubDomain[] = [
  // ---- Module 1: Mission Control
  {
    id: "mission-control",
    module: "mission-control",
    icon: LayoutDashboard,
    labelEn: "Mission Control",
    labelAr: "مركز القيادة",
    defaultBriefAgent: "tashghil",
    screens: [
      { id: "home", route: "/mission-control", labelEn: "Overview", labelAr: "نظرة عامة", status: "shipped" },
    ],
  },

  // ---- Module 2: People
  {
    id: "people-directory",
    module: "people",
    icon: Users,
    labelEn: "People Directory",
    labelAr: "دليل الموظفين",
    defaultBriefAgent: "tashghil",
    screens: [
      { id: "employees", route: "/people/employees", labelEn: "Employees", labelAr: "الموظفون", status: "shipped" },
      { id: "positions", route: "/people/positions", labelEn: "Positions", labelAr: "الوظائف", status: "shipped" },
      { id: "jobs", route: "/people/jobs", labelEn: "Jobs", labelAr: "المسميات الوظيفية", status: "shipped" },
      { id: "org-chart", route: "/people/org-chart", labelEn: "Org Chart", labelAr: "الهيكل التنظيمي", status: "shipped" },
      { id: "contracts", route: "/people/contracts", labelEn: "Contracts", labelAr: "العقود", status: "shipped" },
      { id: "lifecycle-agent", route: "/people/lifecycle-agent", labelEn: "Employee Lifecycle Agent", labelAr: "وكيل دورة حياة الموظف", status: "shipped" },
      { id: "org-design-agent", route: "/people/org-design-agent", labelEn: "Org Design Agent", labelAr: "وكيل تصميم المؤسسة", status: "shipped" },
    ],
  },
  {
    id: "people-watch-lists",
    module: "people",
    icon: ClipboardList,
    labelEn: "Watch Lists",
    labelAr: "قوائم المتابعة",
    defaultBriefAgent: "mutabaa",
    screens: [
      { id: "compliance-alerts", route: "/people/compliance-alerts", labelEn: "Compliance Alerts", labelAr: "تنبيهات الامتثال", status: "shipped" },
      { id: "cchi-compliance", route: "/compliance/benefits-cchi", labelEn: "CCHI Medical Compliance", labelAr: "امتثال التأمين الطبي CCHI", status: "shipped" },
      { id: "beneficiaries", route: "/people/beneficiaries", labelEn: "Beneficiaries", labelAr: "المستفيدون", status: "shipped" },
      { id: "probation-watch", route: "/people/probation-watch", labelEn: "Probation Watch", labelAr: "متابعة فترة التجربة", status: "shipped" },
      { id: "new-joiners", route: "/people/new-joiners", labelEn: "New Joiners", labelAr: "الموظفون الجدد", status: "new-pending" },
      { id: "leavers", route: "/people/leavers", labelEn: "Leavers", labelAr: "المغادرون", status: "new-pending" },
    ],
  },

  // ---- Module 3: Operations
  {
    id: "operations-cross-cutting",
    module: "operations",
    icon: Briefcase,
    labelEn: "HR Operations",
    labelAr: "عمليات الموارد البشرية",
    defaultBriefAgent: "tashghil",
    screens: [
      { id: "hub", route: "/operations/hub", labelEn: "Operations Hub", labelAr: "مركز العمليات", status: "shipped" },
      {
        id: "approvals",
        route: "/operations/approvals",
        labelEn: "Approvals Queue",
        labelAr: "قائمة الموافقات",
        status: "shipped",
        // PR 4 — cross-cutting pending approvals count
        countSource: attentionCounts.crossWorkflowApprovals,
      },
      { id: "import", route: "/operations/import", labelEn: "Import Hub", labelAr: "مركز الاستيراد", status: "shipped" },
    ],
  },
  {
    id: "operations-leaves",
    module: "operations",
    icon: CalendarDays,
    labelEn: "Leaves",
    labelAr: "الإجازات",
    defaultBriefAgent: "tashghil",
    screens: [
      {
        id: "requests",
        route: "/operations/leave/requests",
        labelEn: "Requests",
        labelAr: "الطلبات",
        status: "shipped",
        // PR 4 — pending leave-request count
        countSource: attentionCounts.leaveRequestsSubmitted,
      },
      {
        id: "approvals",
        route: "/operations/leave/approvals",
        labelEn: "Approvals",
        labelAr: "الموافقات",
        status: "shipped",
        // PR 4 — pending leave-approval count
        countSource: attentionCounts.leaveRequestsPendingApproval,
      },
      { id: "calendar", route: "/operations/leave/calendar", labelEn: "Calendar", labelAr: "التقويم", status: "shipped" },
      { id: "balances", route: "/operations/leave/balances", labelEn: "Balances", labelAr: "الأرصدة", status: "shipped" },
      { id: "adjustments", route: "/operations/leave/adjustments", labelEn: "Adjustments", labelAr: "التعديلات", status: "new-pending" },
      { id: "encashment", route: "/operations/leave/encashment", labelEn: "Encashment", labelAr: "الصرف النقدي", status: "new-pending" },
      { id: "carry-forward", route: "/operations/leave/carry-forward", labelEn: "Carry-Forward", labelAr: "ترحيل نهاية السنة", status: "new-pending" },
      { id: "inquiries", route: "/operations/leave/inquiries", labelEn: "Inquiries", labelAr: "الاستفسارات", status: "new-pending" },
      { id: "bulk", route: "/operations/leave/bulk", labelEn: "Bulk Actions", labelAr: "الإجراءات الجماعية", status: "new-pending" },
      { id: "leaves-agent", route: "/operations/leaves-agent", labelEn: "Leaves Agent", labelAr: "وكيل الإجازات", status: "shipped" },
    ],
  },
  {
    id: "operations-benefits",
    module: "operations",
    icon: HeartPulse,
    labelEn: "Benefits",
    labelAr: "المزايا",
    defaultBriefAgent: "tashghil",
    screens: [
      {
        id: "enrollments",
        route: "/operations/benefits-enrollments",
        labelEn: "Enrollments",
        labelAr: "التسجيلات",
        status: "shipped",
        countSource: attentionCounts.benefitEnrollmentsPendingReview,
      },
      {
        id: "dependent-verifications",
        route: "/operations/dependent-verifications",
        labelEn: "Dependent Verifications",
        labelAr: "تحقق المعالين",
        status: "shipped",
        countSource: attentionCounts.dependentVerificationsPending,
      },
      {
        id: "provider-invoices",
        route: "/operations/provider-invoices",
        labelEn: "Provider Invoices",
        labelAr: "فواتير المزود",
        status: "shipped",
        countSource: attentionCounts.providerInvoicesDiscrepancy,
      },
      {
        id: "benefit-claims",
        route: "/operations/benefit-claims",
        labelEn: "Claims Register",
        labelAr: "سجل المطالبات",
        status: "shipped",
        countSource: attentionCounts.benefitClaimsSubmitted,
      },
      {
        id: "benefits-renewal",
        route: "/operations/benefits-renewal",
        labelEn: "Renewal + Retirement",
        labelAr: "التجديد + التقاعد",
        status: "shipped",
        countSource: attentionCounts.renewalPricingFlaggedVariance,
      },
      { id: "benefits-agent", route: "/operations/benefits-agent", labelEn: "Benefits Agent", labelAr: "وكيل المزايا", status: "shipped" },
    ],
  },
  {
    id: "operations-loans",
    module: "operations",
    icon: CreditCard,
    labelEn: "Loans",
    labelAr: "القروض",
    defaultBriefAgent: "tashghil",
    screens: [
      {
        id: "applications",
        route: "/operations/loans/applications",
        labelEn: "Applications",
        labelAr: "الطلبات",
        status: "shipped",
        // PR 4 — pending loan-application count
        countSource: attentionCounts.loanApplicationsPendingApproval,
      },
      { id: "dashboard", route: "/operations/loans", labelEn: "Dashboard", labelAr: "اللوحة", status: "shipped" },
      {
        id: "disbursement-queue",
        route: "/operations/loans/disbursement-queue",
        labelEn: "Disbursement Queue",
        labelAr: "قائمة الصرف",
        status: "new-pending",
        countSource: attentionCounts.loanApplicationsApproved,
      },
      { id: "active-register", route: "/operations/loans/active-register", labelEn: "Active Loans Register", labelAr: "سجل القروض النشطة", status: "new-pending" },
      { id: "settlements", route: "/operations/loans/settlements", labelEn: "Early Settlements", labelAr: "التسويات المبكرة", status: "new-pending" },
      {
        id: "arrears",
        route: "/operations/loans/arrears",
        labelEn: "Arrears",
        labelAr: "المتأخرات",
        status: "new-pending",
        countSource: attentionCounts.loanArrears,
      },
      { id: "article-90-watch", route: "/operations/loans/article-90-watch", labelEn: "Article 90 Watch", labelAr: "متابعة المادة 90", status: "new-pending" },
      { id: "schedules", route: "/operations/loans/schedules", labelEn: "Loan Schedules", labelAr: "جداول القروض", status: "shipped" },
      { id: "loans-agent", route: "/operations/loans-agent", labelEn: "Loans Agent", labelAr: "وكيل القروض", status: "shipped" },
    ],
  },
  {
    id: "operations-excuses",
    module: "operations",
    icon: ClipboardList,
    labelEn: "Excuses",
    labelAr: "الأعذار",
    defaultBriefAgent: "tashghil",
    screens: [
      {
        id: "queue",
        route: "/operations/excuses/queue",
        labelEn: "Queue",
        labelAr: "القائمة",
        status: "shipped",
        countSource: attentionCounts.excuseRequestsPendingApproval,
      },
      { id: "history", route: "/operations/excuses/history", labelEn: "History", labelAr: "السجل", status: "new-pending" },
      { id: "quota-watch", route: "/operations/excuses/quota-watch", labelEn: "Quota Watch", labelAr: "متابعة الحصص", status: "new-pending" },
    ],
  },
  {
    id: "operations-attendance",
    module: "operations",
    icon: ClipboardCheck,
    labelEn: "Attendance",
    labelAr: "الحضور",
    defaultBriefAgent: "tashghil",
    screens: [
      { id: "grid", route: "/operations/attendance", labelEn: "Grid", labelAr: "الشبكة", status: "shipped" },
      { id: "shift-roster", route: "/operations/attendance/shift-roster", labelEn: "Shift Roster", labelAr: "جدول الورديات", status: "new-pending" },
      { id: "overtime-approvals", route: "/operations/attendance/overtime-approvals", labelEn: "Overtime Approvals", labelAr: "موافقات العمل الإضافي", status: "new-pending" },
      { id: "exception-queue", route: "/operations/attendance/exception-queue", labelEn: "Exception Queue", labelAr: "قائمة الاستثناءات", status: "new-pending" },
      { id: "bulk-adjustments", route: "/operations/attendance/bulk-adjustments", labelEn: "Bulk Adjustments", labelAr: "تعديلات جماعية", status: "new-pending" },
      { id: "biometric-sync", route: "/operations/attendance/biometric-sync", labelEn: "Biometric Sync Log", labelAr: "سجل تزامن البصمات", status: "new-pending" },
      { id: "wfh-register", route: "/operations/attendance/wfh-register", labelEn: "WFH / Remote Days", labelAr: "أيام العمل عن بُعد", status: "new-pending" },
      { id: "ot-calculator", route: "/operations/attendance/ot-calculator", labelEn: "OT Calculator (Article 107)", labelAr: "حاسبة الوقت الإضافي (المادة 107)", status: "shipped" },
      { id: "ta-agent", route: "/operations/ta-agent", labelEn: "Time & Attendance Agent", labelAr: "وكيل الوقت والحضور", status: "shipped" },
    ],
  },
  {
    id: "operations-payroll",
    module: "operations",
    icon: Banknote,
    labelEn: "Payroll",
    labelAr: "الرواتب",
    defaultBriefAgent: "rawatib",
    screens: [
      { id: "salary-sheet", route: "/operations/payroll/salary-sheet", labelEn: "Continuous Salary Sheet", labelAr: "كشف الرواتب المستمر", status: "shipped" },
      { id: "period-lock", route: "/operations/payroll/period-lock", labelEn: "Period Lock & Post", labelAr: "إقفال وترحيل الفترة", status: "new-pending" },
      { id: "off-cycle", route: "/operations/payroll/off-cycle", labelEn: "Off-Cycle Disbursements", labelAr: "الصرف خارج الدورة", status: "new-pending" },
      { id: "payslip-distribution", route: "/operations/payroll/payslip-distribution", labelEn: "Payslip Distribution", labelAr: "توزيع كشوف الراتب", status: "new-pending" },
      { id: "wps", route: "/operations/payroll/wps", labelEn: "WPS / Mudad Output", labelAr: "مخرجات WPS / مدد", status: "new-pending" },
      { id: "gl-output", route: "/operations/payroll/gl-output", labelEn: "GL Journal Output", labelAr: "مخرجات دفتر الأستاذ", status: "new-pending" },
      { id: "bank-file", route: "/operations/payroll/bank-file", labelEn: "Bank File Output", labelAr: "ملف البنك", status: "new-pending" },
      { id: "post-history", route: "/operations/payroll/post-history", labelEn: "Post History", labelAr: "سجل الترحيل", status: "new-pending" },
      { id: "dashboard", route: "/operations/payroll", labelEn: "Payroll Dashboard", labelAr: "لوحة الرواتب", status: "shipped" },
      { id: "candidates", route: "/operations/payroll-candidates", labelEn: "Payroll Candidates", labelAr: "مرشحو الرواتب", status: "shipped" },
      { id: "payroll-agent", route: "/operations/payroll-agent", labelEn: "Payroll Agent", labelAr: "وكيل الرواتب", status: "shipped" },
    ],
  },
  {
    id: "operations-travel",
    module: "operations",
    icon: Plane,
    labelEn: "Travel",
    labelAr: "السفر",
    defaultBriefAgent: "tashghil",
    screens: [
      { id: "requests", route: "/operations/transactions/travel", labelEn: "Requests", labelAr: "الطلبات", status: "wire-orphan" },
      { id: "active-travelers", route: "/operations/travel/active-travelers", labelEn: "Active Travelers", labelAr: "المسافرون النشطون", status: "new-pending" },
      { id: "reconciliation", route: "/operations/travel/reconciliation", labelEn: "Pending Reconciliation", labelAr: "التسويات المعلقة", status: "new-pending" },
      { id: "per-diem-disbursements", route: "/operations/travel/per-diem-disbursements", labelEn: "Per Diem Disbursements", labelAr: "صرف بدل السفر", status: "new-pending" },
      { id: "travel-agent", route: "/operations/travel-agent", labelEn: "Business Travel Agent", labelAr: "وكيل السفر التجاري", status: "shipped" },
      { id: "tickets-agent", route: "/operations/tickets-agent", labelEn: "Travel Tickets Agent", labelAr: "وكيل تذاكر السفر", status: "shipped" },
    ],
  },
  {
    id: "operations-eos",
    module: "operations",
    icon: LogOut,
    labelEn: "End of Service",
    labelAr: "نهاية الخدمة",
    defaultBriefAgent: "mutabaa",
    screens: [
      {
        id: "queue",
        route: "/operations/eos",
        labelEn: "Queue",
        labelAr: "القائمة",
        status: "shipped",
        countSource: attentionCounts.eosOpenTerminations,
      },
      { id: "calculation-drafts", route: "/operations/eos/calculation-drafts", labelEn: "Calculation Drafts", labelAr: "مسودات الحسابات", status: "new-pending" },
      { id: "clearance-center", route: "/operations/eos/clearance-center", labelEn: "Exit Clearance Center", labelAr: "مركز إخلاء الطرف", status: "new-pending" },
      { id: "gov-filings-queue", route: "/operations/eos/gov-filings-queue", labelEn: "Gov Filings Queue", labelAr: "قائمة التقديمات الحكومية", status: "new-pending" },
      { id: "final-settlements", route: "/operations/eos/final-settlements", labelEn: "Final Settlements", labelAr: "التسويات النهائية", status: "new-pending" },
      { id: "disputes", route: "/operations/eos-dispute", labelEn: "EOS Disputes", labelAr: "نزاعات نهاية الخدمة", status: "shipped" },
      { id: "absconded-checklist", route: "/operations/eos/absconded-checklist", labelEn: "Absconded Checklist", labelAr: "قائمة فحص المتغيب", status: "shipped" },
      { id: "article-80-wizard", route: "/operations/eos/article-80-classify", labelEn: "Article 80 Wizard", labelAr: "معالج المادة 80", status: "shipped" },
      { id: "deceased-workflow", route: "/operations/eos/deceased-workflow", labelEn: "Deceased Workflow", labelAr: "إجراءات المتوفّى", status: "shipped" },
      { id: "retirement-scanner", route: "/operations/eos/retirement", labelEn: "Retirement Scanner", labelAr: "ماسح التقاعد", status: "shipped" },
    ],
  },
  {
    id: "operations-comp",
    module: "operations",
    icon: TrendingUp,
    labelEn: "Compensation",
    labelAr: "التعويضات",
    defaultBriefAgent: "tashghil",
    // 7 COMP orphans retroactively wired here (M3X.COMP shipped on legacy chassis 2026-05-24)
    screens: [
      { id: "comp-agent", route: "/operations/comp-agent", labelEn: "Comp Management Agent", labelAr: "وكيل إدارة التعويضات", status: "shipped" },
      { id: "comp-changes", route: "/operations/comp-changes", labelEn: "Comp Changes Queue", labelAr: "قائمة تغييرات التعويضات", status: "wire-orphan" },
      { id: "comp-changes-new", route: "/operations/comp-changes/new", labelEn: "New Comp Change", labelAr: "تغيير تعويضات جديد", status: "wire-orphan" },
      { id: "comp-cycles", route: "/operations/comp-cycles", labelEn: "Annual Merit Cycles", labelAr: "دورات تقدير الجدارة السنوية", status: "wire-orphan" },
      { id: "commission-structures", route: "/operations/commission-structures", labelEn: "Commission Structures", labelAr: "هياكل العمولات", status: "wire-orphan" },
      { id: "comp-cycle-list", route: "/operations/comp-cycle", labelEn: "Compensation Cycles List", labelAr: "قائمة دورات التعويضات", status: "shipped" },
      { id: "comp-transactions", route: "/operations/transactions/comp", labelEn: "Compensation Transactions", labelAr: "معاملات التعويضات", status: "shipped" },
    ],
  },

  // ---- Module 4: Policies
  {
    id: "policies-leaves",
    module: "policies",
    icon: CalendarDays,
    labelEn: "Leave Policies",
    labelAr: "سياسات الإجازات",
    defaultBriefAgent: "mutabaa",
    screens: [
      { id: "types", route: "/policies/leave-types", labelEn: "Leave Types", labelAr: "أنواع الإجازات", status: "shipped" },
      { id: "accrual-rules", route: "/policies/leave/accrual-rules", labelEn: "Accrual Rules", labelAr: "قواعد الاستحقاق", status: "new-pending" },
      { id: "eligibility-matrix", route: "/policies/leave/eligibility-matrix", labelEn: "Eligibility Matrix", labelAr: "مصفوفة الأهلية", status: "new-pending" },
      { id: "encashment-rules", route: "/policies/leave/encashment-rules", labelEn: "Encashment Rules", labelAr: "قواعد الصرف النقدي", status: "new-pending" },
      { id: "carry-forward-rules", route: "/policies/leave/carry-forward-rules", labelEn: "Carry-Forward Rules", labelAr: "قواعد الترحيل", status: "new-pending" },
      { id: "year-calendar", route: "/policies/leave/year-calendar", labelEn: "Leave Year Calendar", labelAr: "تقويم سنة الإجازات", status: "new-pending" },
    ],
  },
  {
    id: "policies-loans",
    module: "policies",
    icon: CreditCard,
    labelEn: "Loan Policies",
    labelAr: "سياسات القروض",
    defaultBriefAgent: "mutabaa",
    screens: [
      { id: "products", route: "/policies/loan-products", labelEn: "Loan Products", labelAr: "منتجات القروض", status: "shipped" },
      { id: "eligibility-rules", route: "/policies/loans/eligibility-rules", labelEn: "Eligibility Rules", labelAr: "قواعد الأهلية", status: "new-pending" },
      { id: "article-90-config", route: "/policies/loans/article-90-config", labelEn: "Article 90 Cap Config", labelAr: "إعدادات سقف المادة 90", status: "new-pending" },
      { id: "settlement-methods", route: "/policies/loans/settlement-methods", labelEn: "Settlement Methods", labelAr: "طرق التسوية", status: "new-pending" },
    ],
  },
  {
    id: "policies-attendance",
    module: "policies",
    icon: ClipboardCheck,
    labelEn: "Attendance & Shifts",
    labelAr: "الحضور والورديات",
    defaultBriefAgent: "mutabaa",
    screens: [
      { id: "shifts", route: "/policies/shifts", labelEn: "Shifts", labelAr: "الورديات", status: "shipped" },
      { id: "patterns", route: "/policies/shifts/patterns", labelEn: "Shift Patterns", labelAr: "أنماط الورديات", status: "new-pending" },
      { id: "overtime-rules", route: "/policies/shifts/overtime-rules", labelEn: "Overtime Premium Rules", labelAr: "قواعد بدل العمل الإضافي", status: "new-pending" },
      { id: "grace-rules", route: "/policies/shifts/grace-rules", labelEn: "Late Tolerance / Grace", labelAr: "مهلة التأخير", status: "new-pending" },
      { id: "biometric-devices", route: "/policies/shifts/biometric-devices", labelEn: "Biometric Device Catalog", labelAr: "كتالوج أجهزة البصمة", status: "new-pending" },
      { id: "geofences", route: "/policies/shifts/geofences", labelEn: "Geofence Definitions", labelAr: "تعريفات النطاق الجغرافي", status: "new-pending" },
      { id: "shift-roster", route: "/policies/shifts/roster", labelEn: "Shift Roster", labelAr: "جدول المناوبات", status: "shipped" },
      { id: "shift-rotations", route: "/policies/shifts/rotations", labelEn: "Shift Rotation Patterns", labelAr: "أنماط دوران المناوبات", status: "shipped" },
    ],
  },
  {
    id: "policies-payroll",
    module: "policies",
    icon: Banknote,
    labelEn: "Payroll Policies",
    labelAr: "سياسات الرواتب",
    defaultBriefAgent: "rawatib",
    screens: [
      { id: "earnings-catalog", route: "/policies/payroll/earnings-catalog", labelEn: "Earnings Catalog", labelAr: "كتالوج الأرباح", status: "new-pending" },
      { id: "deductions-catalog", route: "/policies/payroll/deductions-catalog", labelEn: "Deductions Catalog", labelAr: "كتالوج الاستقطاعات", status: "new-pending" },
      { id: "calendars", route: "/policies/payroll/calendars", labelEn: "Payroll Calendars", labelAr: "تقاويم الرواتب", status: "new-pending" },
      { id: "gosi-rates", route: "/policies/payroll/gosi-rates", labelEn: "GOSI Rate Tables", labelAr: "جداول معدلات التأمينات", status: "new-pending" },
      { id: "gl-mapping", route: "/policies/payroll/gl-mapping", labelEn: "GL Mapping", labelAr: "ربط دفتر الأستاذ", status: "new-pending" },
      { id: "bank-templates", route: "/policies/payroll/bank-templates", labelEn: "Bank Format Templates", labelAr: "قوالب صيغ البنوك", status: "new-pending" },
      { id: "tax-withholding", route: "/policies/payroll/tax-withholding", labelEn: "Tax / Withholding", labelAr: "الضريبة / الاستقطاع", status: "new-pending" },
      { id: "commission-structures", route: "/policies/commission-structures", labelEn: "Commission Structures", labelAr: "هياكل العمولات", status: "shipped" },
      { id: "comp-bands", route: "/policies/comp-bands", labelEn: "Compensation Bands", labelAr: "نطاقات التعويضات", status: "shipped" },
    ],
  },
  {
    id: "policies-travel",
    module: "policies",
    icon: Plane,
    labelEn: "Travel Policies",
    labelAr: "سياسات السفر",
    defaultBriefAgent: "mutabaa",
    screens: [
      { id: "per-diem", route: "/policies/travel-per-diem", labelEn: "Per Diem Matrix", labelAr: "مصفوفة بدل السفر", status: "wire-orphan" },
      { id: "categories", route: "/policies/travel/categories", labelEn: "Travel Categories", labelAr: "فئات السفر", status: "new-pending" },
      { id: "destinations", route: "/policies/travel/destinations", labelEn: "Destination Catalog", labelAr: "كتالوج الوجهات", status: "new-pending" },
      { id: "advance-reconciliation", route: "/policies/travel/advance-reconciliation", labelEn: "Advance / Reconciliation %", labelAr: "نسب السلفة / التسوية", status: "new-pending" },
      { id: "booking-class-rules", route: "/policies/travel/booking-class-rules", labelEn: "Booking Class Rules", labelAr: "قواعد درجة الحجز", status: "new-pending" },
    ],
  },
  {
    id: "policies-eos",
    module: "policies",
    icon: LogOut,
    labelEn: "EOS Policies",
    labelAr: "سياسات نهاية الخدمة",
    defaultBriefAgent: "mutabaa",
    screens: [
      { id: "calculation-rules", route: "/policies/eos/calculation-rules", labelEn: "EOS Calculation Rules", labelAr: "قواعد حساب نهاية الخدمة", status: "new-pending" },
      { id: "termination-reasons", route: "/policies/eos/termination-reasons", labelEn: "Termination Reason Codes", labelAr: "رموز أسباب الإنهاء", status: "new-pending" },
      { id: "notice-period-rules", route: "/policies/eos/notice-period-rules", labelEn: "Notice Period Rules", labelAr: "قواعد فترة الإشعار", status: "new-pending" },
      { id: "rate-matrix", route: "/policies/eos-rate-matrix", labelEn: "EOS Rate Matrix", labelAr: "مصفوفة معدلات نهاية الخدمة", status: "shipped" },
      { id: "rate-matrix-autoflush", route: "/policies/eos-rate-matrix/autoflush", labelEn: "EOS Onboarding Autoflush", labelAr: "تدفق إعداد نهاية الخدمة", status: "shipped" },
    ],
  },
  {
    id: "policies-cross-cutting",
    module: "policies",
    icon: ShieldCheck,
    labelEn: "Cross-cutting Policies",
    labelAr: "سياسات شاملة",
    defaultBriefAgent: "mutabaa",
    screens: [
      { id: "grades", route: "/policies/grades", labelEn: "Grades", labelAr: "الدرجات", status: "shipped" },
      { id: "allowances", route: "/policies/allowances", labelEn: "Allowances", labelAr: "البدلات", status: "shipped" },
      { id: "benefits-plans", route: "/policies/benefits-plans", labelEn: "Benefits Plans", labelAr: "خطط المزايا", status: "shipped" },
      { id: "excuse-types", route: "/policies/excuse-types", labelEn: "Excuse Types", labelAr: "أنواع الأعذار", status: "shipped" },
      { id: "approval-chains", route: "/policies/approval-chains", labelEn: "Approval Chains", labelAr: "سلاسل الموافقات", status: "shipped" },
      { id: "holiday-calendar", route: "/policies/holiday-calendar", labelEn: "Holiday Calendar", labelAr: "تقويم العطل", status: "shipped" },
      { id: "ai-ingestion", route: "/policies/ai-ingestion", labelEn: "AI Ingestion", labelAr: "استيعاب الذكاء الاصطناعي", status: "shipped" },
      { id: "compliance-rules", route: "/policies/compliance-rules", labelEn: "Compliance Rules", labelAr: "قواعد الامتثال", status: "shipped" },
      { id: "government-layer", route: "/policies/government-layer", labelEn: "Government Layer", labelAr: "الطبقة الحكومية", status: "shipped" },
      { id: "grading-agent", route: "/policies/grading-agent", labelEn: "Grading Agent", labelAr: "وكيل التدرّج", status: "shipped" },
    ],
  },

  // ---- Module 5: AI
  {
    id: "ai-agents",
    module: "ai",
    icon: Sparkles,
    labelEn: "Agents",
    labelAr: "الوكلاء",
    defaultBriefAgent: "tashghil",
    screens: [
      { id: "list", route: "/ai/agents", labelEn: "Agent Registry", labelAr: "سجل الوكلاء", status: "shipped" },
      { id: "proposals", route: "/ai/proposals", labelEn: "Agent Proposals", labelAr: "اقتراحات الوكلاء", status: "shipped" },
    ],
  },
  {
    id: "ai-configuration",
    module: "ai",
    icon: SettingsIcon,
    labelEn: "Configuration",
    labelAr: "الإعدادات",
    defaultBriefAgent: "tashghil",
    screens: [
      { id: "behavior", route: "/ai/behavior", labelEn: "Behavior", labelAr: "السلوك", status: "shipped" },
      { id: "boundaries", route: "/ai/boundaries", labelEn: "Boundaries", labelAr: "الحدود", status: "shipped" },
      { id: "persona", route: "/ai/persona", labelEn: "Persona", labelAr: "الشخصية", status: "shipped" },
      { id: "knowledge", route: "/ai/knowledge", labelEn: "Knowledge", labelAr: "المعرفة", status: "shipped" },
      { id: "sandbox", route: "/ai/sandbox", labelEn: "Sandbox", labelAr: "بيئة التجربة", status: "shipped" },
      // Integrations Discoverability hotfix (Option B):
      // Credentials live with the agent that uses them. These deep-
      // link into Settings → Integrations with the matching card
      // scrolled-to + ring-highlighted (see IntegrationsList's
      // useScrollToHash). Mudad / GOSI / Qiwa / Muqeem are
      // government — they land with M3X.GOV.
      { id: "ai-provider", route: "/settings/integrations#anthropic", labelEn: "AI Provider", labelAr: "مزوّد الذكاء الاصطناعي", status: "shipped" },
      { id: "concierge-channel", route: "/settings/integrations#twilio", labelEn: "Concierge Channel", labelAr: "قناة المساعد", status: "shipped" },
    ],
  },
  {
    id: "ai-chat",
    module: "ai",
    icon: Bot,
    labelEn: "Chat",
    labelAr: "المحادثة",
    defaultBriefAgent: "khidmat",
    screens: [
      { id: "threads", route: "/ai/threads", labelEn: "Threads", labelAr: "المحادثات", status: "shipped" },
      { id: "chat-drafts", route: "/ai/chat-drafts", labelEn: "Drafts", labelAr: "المسودات", status: "shipped" },
      { id: "stuck-inbox", route: "/ai/stuck-inbox", labelEn: "Stuck Inbox", labelAr: "صندوق الوارد العالق", status: "shipped" },
    ],
  },
  {
    id: "ai-diagnostics",
    module: "ai",
    icon: Activity,
    labelEn: "Diagnostics",
    labelAr: "التشخيصات",
    defaultBriefAgent: "mutabaa",
    screens: [
      { id: "reasoning", route: "/ai/reasoning", labelEn: "Reasoning Traces", labelAr: "تتبع الاستدلال", status: "wire-orphan" },
      { id: "decision-registry", route: "/ai/decision-registry", labelEn: "Decision Registry", labelAr: "سجل القرارات", status: "new-pending" },
      { id: "scanner-health", route: "/ai/scanner-health", labelEn: "Scanner Health", labelAr: "صحة الماسحات", status: "new-pending" },
      { id: "feedback", route: "/ai/feedback", labelEn: "AI Feedback", labelAr: "ملاحظات الذكاء", status: "shipped" },
    ],
  },

  // ---- Module 6: Reports
  {
    id: "reports-library",
    module: "reports",
    icon: Library,
    labelEn: "Reports Library",
    labelAr: "مكتبة التقارير",
    defaultBriefAgent: "tashghil",
    screens: [
      { id: "overview", route: "/reports", labelEn: "Overview", labelAr: "نظرة عامة", status: "shipped" },
      { id: "dashboards", route: "/reports/dashboards", labelEn: "Dashboards", labelAr: "اللوحات", status: "shipped" },
      { id: "headcount", route: "/reports/headcount", labelEn: "Headcount Dashboard", labelAr: "لوحة الموظفين", status: "shipped" },
      { id: "turnover", route: "/reports/turnover", labelEn: "Turnover Dashboard", labelAr: "لوحة دوران الموظفين", status: "shipped" },
      { id: "compensation", route: "/reports/compensation", labelEn: "Compensation Dashboard", labelAr: "لوحة التعويضات", status: "shipped" },
      { id: "compliance", route: "/reports/compliance", labelEn: "Compliance Dashboard", labelAr: "لوحة الامتثال", status: "shipped" },
      { id: "benefits", route: "/reports/benefits", labelEn: "Benefits Reports", labelAr: "تقارير المزايا", status: "shipped" },
    ],
  },
  {
    id: "reports-domain",
    module: "reports",
    icon: ChartLine,
    labelEn: "Domain Reports",
    labelAr: "تقارير المجال",
    defaultBriefAgent: "tashghil",
    screens: [
      // M3X.REPORTS §0.8 — the 11 per-domain Rail 2 sub-entries plus
      // the 3 cross-domain Rail 2 entries. Dashboards lives in the
      // separate "reports-library" group above; tools live in the
      // "reports-tools" group below. Total Rail 2 = 19.
      { id: "leaves", route: "/reports/leaves", labelEn: "Leaves Reports", labelAr: "تقارير الإجازات", status: "shipped" },
      { id: "payroll", route: "/reports/payroll", labelEn: "Payroll Reports", labelAr: "تقارير الرواتب", status: "shipped" },
      { id: "loans", route: "/reports/loans", labelEn: "Loans Reports", labelAr: "تقارير القروض", status: "shipped" },
      { id: "excuses", route: "/reports/excuses", labelEn: "Excuses Reports", labelAr: "تقارير الاستئذانات", status: "shipped" },
      { id: "attendance", route: "/reports/attendance", labelEn: "Attendance Reports", labelAr: "تقارير الحضور", status: "shipped" },
      { id: "eos", route: "/reports/eos", labelEn: "EOS Reports", labelAr: "تقارير نهاية الخدمة", status: "shipped" },
      { id: "travel", route: "/reports/travel", labelEn: "Travel Reports", labelAr: "تقارير السفر", status: "shipped" },
      { id: "comp", route: "/reports/comp", labelEn: "Comp Reports", labelAr: "تقارير التعويضات", status: "shipped" },
      { id: "benefits", route: "/reports/benefits", labelEn: "Benefits Reports", labelAr: "تقارير المزايا", status: "shipped" },
      { id: "employee", route: "/reports/employee", labelEn: "Employee Reports", labelAr: "تقارير الموظفين", status: "shipped" },
      { id: "org", route: "/reports/org", labelEn: "Org Reports", labelAr: "تقارير الهيكل", status: "shipped" },
      { id: "analytics", route: "/reports/analytics", labelEn: "Analytics", labelAr: "التحليلات", status: "shipped" },
      { id: "compliance-audits", route: "/reports/compliance-list", labelEn: "Compliance Reports", labelAr: "تقارير الامتثال", status: "shipped" },
      { id: "agents", route: "/reports/agents", labelEn: "AI / Agents", labelAr: "الذكاء الاصطناعي / الوكلاء", status: "shipped" },
    ],
  },
  {
    id: "reports-tools",
    module: "reports",
    icon: PieChart,
    labelEn: "Library Tools",
    labelAr: "أدوات المكتبة",
    defaultBriefAgent: "tashghil",
    screens: [
      { id: "custom-builder", route: "/reports/custom-builder", labelEn: "Custom Builder", labelAr: "منشئ التقارير", status: "shipped" },
      { id: "saved", route: "/reports/saved", labelEn: "Saved Reports", labelAr: "التقارير المحفوظة", status: "shipped" },
      { id: "scheduled", route: "/reports/scheduled", labelEn: "Scheduled Reports", labelAr: "التقارير المجدولة", status: "shipped" },
      { id: "exports", route: "/reports/exports", labelEn: "Past Exports", labelAr: "التصديرات السابقة", status: "shipped" },
      { id: "exports-log", route: "/reports/exports-log", labelEn: "Exports Log", labelAr: "سجل الصادرات", status: "shipped" },
      { id: "agent", route: "/reports/agent", labelEn: "Reports Agent", labelAr: "وكيل التقارير", status: "shipped" },
    ],
  },

  // ---- Module 7: Settings (entries below) — but first the "me" workspace

  // ---- Module 8 ("me" workspace) — F.31-F.40 personal screens
  {
    id: "me-hub",
    module: "me",
    icon: LayoutDashboard,
    labelEn: "My Hub",
    labelAr: "صفحتي",
    defaultBriefAgent: "khidmat",
    screens: [
      { id: "home", route: "/me", labelEn: "My Hub", labelAr: "صفحتي", status: "new-pending" },
    ],
  },
  {
    id: "me-leaves",
    module: "me",
    icon: CalendarDays,
    labelEn: "My Leaves",
    labelAr: "إجازاتي",
    defaultBriefAgent: "khidmat",
    screens: [
      { id: "hub", route: "/me/leaves", labelEn: "Overview", labelAr: "نظرة عامة", status: "shipped" },
      { id: "request", route: "/me/leaves/request", labelEn: "Request New", labelAr: "طلب جديد", status: "new-pending" },
      { id: "my-requests", route: "/me/leaves/my-requests", labelEn: "My Requests", labelAr: "طلباتي", status: "new-pending" },
      { id: "balances", route: "/me/leaves/balances", labelEn: "My Balances", labelAr: "أرصدتي", status: "new-pending" },
      { id: "calendar", route: "/me/leaves/calendar", labelEn: "My Calendar", labelAr: "تقويمي", status: "new-pending" },
      { id: "adjustments", route: "/me/leaves/adjustments", labelEn: "Adjustments History", labelAr: "سجل التعديلات", status: "new-pending" },
    ],
  },
  {
    id: "me-payments",
    module: "me",
    icon: Banknote,
    labelEn: "My Payment Hub",
    labelAr: "مركز دفعاتي",
    defaultBriefAgent: "rawatib",
    screens: [
      { id: "hub", route: "/me/payment-hub", labelEn: "Overview", labelAr: "نظرة عامة", status: "shipped" },
      { id: "latest", route: "/me/payments/latest", labelEn: "Latest Payslip", labelAr: "أحدث قسيمة راتب", status: "new-pending" },
      { id: "archive", route: "/me/payments/archive", labelEn: "Payslip Archive", labelAr: "أرشيف قسائم الراتب", status: "new-pending" },
      { id: "ytd", route: "/me/payments/ytd", labelEn: "YTD Statement", labelAr: "كشف منذ بداية السنة", status: "new-pending" },
      { id: "deductions", route: "/me/payments/deductions", labelEn: "Deductions Breakdown", labelAr: "تفصيل الاستقطاعات", status: "new-pending" },
      { id: "bank", route: "/me/payments/bank", labelEn: "Bank Details", labelAr: "بيانات البنك", status: "new-pending" },
      { id: "gosi", route: "/me/payments/gosi", labelEn: "GOSI Statement", labelAr: "كشف التأمينات", status: "new-pending" },
    ],
  },
  {
    id: "me-loans",
    module: "me",
    icon: CreditCard,
    labelEn: "My Loans",
    labelAr: "قروضي",
    defaultBriefAgent: "khidmat",
    screens: [
      { id: "hub", route: "/me/loans", labelEn: "Overview", labelAr: "نظرة عامة", status: "shipped" },
      { id: "apply", route: "/me/loans/apply", labelEn: "Apply New", labelAr: "طلب قرض جديد", status: "new-pending" },
      { id: "applications", route: "/me/loans/applications", labelEn: "My Applications", labelAr: "طلباتي", status: "new-pending" },
      { id: "active", route: "/me/loans/active", labelEn: "My Active Loans", labelAr: "قروضي النشطة", status: "new-pending" },
      { id: "schedule", route: "/me/loans/schedule", labelEn: "Repayment Schedule", labelAr: "جدول السداد", status: "new-pending" },
      { id: "settlement-calc", route: "/me/loans/settlement-calc", labelEn: "Early Settlement Calc", labelAr: "حاسبة التسوية المبكرة", status: "new-pending" },
      { id: "history", route: "/me/loans/history", labelEn: "History", labelAr: "السجل", status: "new-pending" },
    ],
  },
  {
    id: "me-attendance",
    module: "me",
    icon: ClipboardCheck,
    labelEn: "My Attendance",
    labelAr: "حضوري",
    defaultBriefAgent: "khidmat",
    screens: [
      { id: "hub", route: "/me/attendance", labelEn: "Overview", labelAr: "نظرة عامة", status: "shipped" },
      { id: "today", route: "/me/attendance/today", labelEn: "Today's Status", labelAr: "حالة اليوم", status: "new-pending" },
      { id: "period", route: "/me/attendance/period", labelEn: "This Period", labelAr: "هذه الفترة", status: "new-pending" },
      { id: "excuses", route: "/me/attendance/excuses", labelEn: "My Excuses", labelAr: "أعذاري", status: "new-pending" },
      { id: "overtime", route: "/me/attendance/overtime", labelEn: "My Overtime", labelAr: "ساعات العمل الإضافي", status: "new-pending" },
      { id: "shift", route: "/me/attendance/shift", labelEn: "My Shift", labelAr: "ورديتي", status: "new-pending" },
    ],
  },
  {
    id: "me-travel-personal",
    module: "me",
    icon: Plane,
    labelEn: "My Travel",
    labelAr: "سفري",
    defaultBriefAgent: "khidmat",
    screens: [
      { id: "hub", route: "/me/travel", labelEn: "Overview", labelAr: "نظرة عامة", status: "shipped" },
      { id: "request", route: "/me/travel/request", labelEn: "Request New Trip", labelAr: "طلب سفر جديد", status: "new-pending" },
      { id: "active", route: "/me/travel/active", labelEn: "My Active Trip", labelAr: "رحلتي النشطة", status: "new-pending" },
      { id: "reconciliation", route: "/me/travel/reconciliation", labelEn: "Pending Reconciliation", labelAr: "التسويات المعلقة", status: "new-pending" },
      { id: "history", route: "/me/travel/history", labelEn: "Past Trips", labelAr: "الرحلات السابقة", status: "new-pending" },
    ],
  },
  {
    id: "me-documents",
    module: "me",
    icon: ClipboardList,
    labelEn: "My Documents",
    labelAr: "وثائقي",
    defaultBriefAgent: "khidmat",
    screens: [
      { id: "list", route: "/me/documents", labelEn: "Documents", labelAr: "الوثائق", status: "new-pending" },
    ],
  },
  {
    id: "me-eos",
    module: "me",
    icon: LogOut,
    labelEn: "My EOS",
    labelAr: "نهاية خدمتي",
    defaultBriefAgent: "khidmat",
    screens: [
      { id: "hub", route: "/me/end-of-service", labelEn: "Overview", labelAr: "نظرة عامة", status: "shipped" },
      { id: "projection", route: "/me/eos", labelEn: "EOS Projection", labelAr: "إسقاط نهاية الخدمة", status: "new-pending" },
    ],
  },
  {
    id: "me-team",
    module: "me",
    icon: Users,
    labelEn: "My Team",
    labelAr: "فريقي",
    defaultBriefAgent: "khidmat",
    screens: [
      { id: "direct-reports", route: "/me/team", labelEn: "Direct Reports", labelAr: "المرؤوسون المباشرون", status: "new-pending" },
    ],
  },
  {
    id: "me-profile",
    module: "me",
    icon: UserCog,
    labelEn: "My Profile",
    labelAr: "ملفي الشخصي",
    defaultBriefAgent: "khidmat",
    screens: [
      { id: "edit", route: "/me/profile", labelEn: "Profile", labelAr: "الملف الشخصي", status: "new-pending" },
    ],
  },

  // ---- Module 7: Settings (resumes here)
  {
    id: "settings-tenant-org",
    module: "settings",
    icon: Building2,
    labelEn: "Tenant & Organization",
    labelAr: "المستأجر والمؤسسة",
    defaultBriefAgent: "tashghil",
    screens: [
      { id: "tenant", route: "/settings/tenant", labelEn: "Tenant Identity", labelAr: "هوية المستأجر", status: "shipped" },
      { id: "legal-entities", route: "/settings/legal-entities", labelEn: "Legal Entities", labelAr: "الكيانات القانونية", status: "shipped" },
      // 2026-05-30 nav audit — these were on the /settings landing page
      // but missing from the sidebar. Restored so every shipped page is
      // reachable from the menu, not only by deep-linking.
      { id: "legal-entity-agent", route: "/settings/legal-entity-agent", labelEn: "Legal Entity Agent", labelAr: "وكيل الكيان القانوني", status: "shipped" },
      { id: "branding", route: "/settings/branding", labelEn: "Branding", labelAr: "العلامة التجارية", status: "shipped" },
    ],
  },
  {
    id: "settings-users-roles",
    module: "settings",
    icon: UserCog,
    labelEn: "Users & Roles",
    labelAr: "المستخدمون والأدوار",
    defaultBriefAgent: "tashghil",
    screens: [
      // 2026-05-30 nav audit — route was `/settings/users-roles` which
      // resolves to the RegistryDrivenPage placeholder. Real page lives
      // at `/settings/users`.
      { id: "users", route: "/settings/users", labelEn: "Users & Roles", labelAr: "المستخدمون والأدوار", status: "shipped" },
      { id: "notifications", route: "/settings/notifications", labelEn: "Notifications", labelAr: "الإشعارات", status: "shipped" },
    ],
  },
  {
    id: "settings-system",
    module: "settings",
    icon: KeySquare,
    labelEn: "System",
    labelAr: "النظام",
    defaultBriefAgent: "tashghil",
    screens: [
      // 2026-05-31 — Onboarding Journey must stay reachable after
      // go-live so HR can revisit the 9-step setup. Was hidden from
      // the menu (lived only on the Mission Control progress widget
      // pre-go-live).
      { id: "onboarding-journey", route: "/onboarding", labelEn: "Onboarding Journey", labelAr: "رحلة الإعداد", status: "shipped" },
      { id: "imports", route: "/settings/imports", labelEn: "Imports", labelAr: "الاستيرادات", status: "shipped" },
      { id: "audit-log", route: "/settings/audit-log", labelEn: "Audit Log", labelAr: "سجل التدقيق", status: "shipped" },
      { id: "billing", route: "/settings/billing", labelEn: "Billing", labelAr: "الفواتير", status: "shipped" },
      { id: "statutory-defaults", route: "/settings/statutory-defaults", labelEn: "Statutory Defaults", labelAr: "الافتراضيات القانونية", status: "shipped" },
    ],
  },
  // M3X.PLATFORM-MINIMAL PR 10-11 — Integration Hub
  {
    id: "settings-platform",
    module: "settings",
    icon: KeySquare,
    labelEn: "Platform & Integrations",
    labelAr: "المنصة والتكاملات",
    defaultBriefAgent: "tashghil",
    screens: [
      // Promoted from the hidden roster: the master integrations list
      // is the first entry every user reaches for. Per-integration
      // sub-pages (Mudad / GOSI / Qiwa / Muqeem) are reached from the
      // Government Integrations hub one level down — they were menu
      // duplicates before this cleanup.
      { id: "integrations", route: "/settings/integrations", labelEn: "Integrations", labelAr: "التكاملات", status: "shipped" },
      { id: "pats", route: "/settings/security/personal-access-tokens", labelEn: "Personal Access Tokens", labelAr: "رموز الوصول الشخصية", status: "shipped" },
      { id: "power-bi", route: "/settings/integrations/power-bi", labelEn: "Power BI", labelAr: "Power BI", status: "shipped" },
      { id: "mcp-beta", route: "/settings/integrations/mcp-beta", labelEn: "MCP-beta", labelAr: "MCP (تجريبي)", status: "shipped" },
      // M3X.GOV — single hub. The 4 KSA portals (Mudad / GOSI / Qiwa /
      // Muqeem) are tabs INSIDE this hub, not menu entries.
      { id: "gov-hub", route: "/settings/integrations/gov", labelEn: "Government Integrations", labelAr: "التكاملات الحكومية", status: "shipped" },
      // M3X.SIDEBAR-DISCOVERABILITY 2026-05-31 — the 4 KSA portal pages
      // were previously only reachable from the Gov hub tabs. Surfacing
      // them as direct sidebar entries per Amer's discoverability ask.
      { id: "gov-mudad", route: "/settings/integrations/gov/mudad", labelEn: "Mudad / WPS", labelAr: "مدد · نظام حماية الأجور", status: "shipped" },
      { id: "gov-gosi", route: "/settings/integrations/gov/gosi", labelEn: "GOSI", labelAr: "التأمينات الاجتماعية", status: "shipped" },
      { id: "gov-qiwa", route: "/settings/integrations/gov/qiwa", labelEn: "Qiwa", labelAr: "قوى", status: "shipped" },
      { id: "gov-muqeem", route: "/settings/integrations/gov/muqeem", labelEn: "Muqeem", labelAr: "مقيم", status: "shipped" },
      { id: "biometric", route: "/integrations/biometric", labelEn: "Biometric Machines", labelAr: "أجهزة البصمة", status: "shipped" },
      { id: "docs", route: "/settings/integrations/docs", labelEn: "Integration Docs", labelAr: "وثائق التكامل", status: "shipped" },
    ],
  },

];


// ---------------------------------------------------------------------------
// Query helpers
// ---------------------------------------------------------------------------


export function subDomainsForModule(module: ModuleId): SubDomain[] {
  return NAVIGATION_REGISTRY.filter((sd) => sd.module === module);
}


export function subDomainForRoute(pathname: string): SubDomain | null {
  let bestMatch: { sd: SubDomain; routeLen: number } | null = null;
  for (const sd of NAVIGATION_REGISTRY) {
    for (const screen of sd.screens) {
      const base = screen.route.split(":")[0].replace(/\/$/, "");
      if (pathname === base || pathname.startsWith(base + "/")) {
        if (!bestMatch || base.length > bestMatch.routeLen) {
          bestMatch = { sd, routeLen: base.length };
        }
      }
    }
  }
  return bestMatch?.sd ?? null;
}


export function screenForRoute(
  pathname: string,
): { subDomain: SubDomain; screen: Screen } | null {
  let bestMatch:
    | { subDomain: SubDomain; screen: Screen; routeLen: number }
    | null = null;
  for (const sd of NAVIGATION_REGISTRY) {
    for (const screen of sd.screens) {
      const base = screen.route.split(":")[0].replace(/\/$/, "");
      if (pathname === base || pathname.startsWith(base + "/")) {
        if (!bestMatch || base.length > bestMatch.routeLen) {
          bestMatch = { subDomain: sd, screen, routeLen: base.length };
        }
      }
    }
  }
  if (!bestMatch) return null;
  return { subDomain: bestMatch.subDomain, screen: bestMatch.screen };
}


export function allRegisteredRoutes(): string[] {
  const nav = NAVIGATION_REGISTRY.flatMap((sd) =>
    sd.screens.map((s) => s.route),
  );
  return [...nav, ...INTERNAL_ROUTES.map((r) => r.route)];
}

// ---------------------------------------------------------------------------
// INTERNAL_ROUTES — Session 3e PR 3 explicit bypass registry.
// ---------------------------------------------------------------------------
//
// Routes here are NOT surfaced in any menu walk. They register here
// so the orphan-lint can flip from grandfather mode to full enforcement
// without losing track of system / auth / Clerk callback / admin /
// Navigate-redirect / module-landing / detail / onboarding-step routes.
//
// Adding a new internal route: include the {route, reason, internalOnly:true,
// labelEn, labelAr} shape. The reason is MANDATORY — explains why this
// route bypasses the menu (auth / detail-of-list-page / admin-only / etc.).
//
// Adding a new USER-FACING route → put it in NAVIGATION_REGISTRY above,
// NOT here. The lint catches this.

export const INTERNAL_ROUTES: Screen[] = [
  // ---- Module landings (Rail 1 click target, not a sub-domain menu item) ----
  { id: "module-landing-ai", route: "/ai", labelEn: "AI", labelAr: "الذكاء الاصطناعي", internalOnly: true, reason: "Module landing — Rail 1 click target, not a sub-domain menu item." },
  { id: "module-landing-people", route: "/people", labelEn: "People", labelAr: "الأشخاص", internalOnly: true, reason: "Module landing — Rail 1 click target." },
  { id: "module-landing-operations", route: "/operations", labelEn: "Operations", labelAr: "العمليات", internalOnly: true, reason: "Module landing — Rail 1 click target." },
  { id: "module-landing-policies", route: "/policies", labelEn: "Policies", labelAr: "السياسات", internalOnly: true, reason: "Module landing — Rail 1 click target." },
  { id: "module-landing-settings", route: "/settings", labelEn: "Settings", labelAr: "الإعدادات", internalOnly: true, reason: "Module landing — Rail 1 click target." },

  // ---- Module sub-domain landings reached via Rail 2 click, not screen menu ----
  { id: "operations-leave-landing", route: "/operations/leave", labelEn: "Leave", labelAr: "الإجازة", internalOnly: true, reason: "Sub-domain landing — Rail 2 click target." },
  { id: "operations-excuses-landing", route: "/operations/excuses", labelEn: "Excuses", labelAr: "الأعذار", internalOnly: true, reason: "Sub-domain landing — Rail 2 click target." },
  { id: "operations-payroll-landing", route: "/operations/payroll", labelEn: "Payroll", labelAr: "الرواتب", internalOnly: true, reason: "Continuous accumulator — Rail 2 click target (M3X.PAYROLL Rule 5: no Generate button)." },

  // ---- Agent landing pages (sidebar Rail 2 click targets, not menu items) ----
  { id: "agent-people-lifecycle", route: "/people/lifecycle-agent", labelEn: "Employee Lifecycle Agent", labelAr: "وكيل دورة حياة الموظف", internalOnly: true, reason: "Agent landing page reached from People sub-domain." },
  { id: "agent-people-org-design", route: "/people/org-design-agent", labelEn: "Org Design Agent", labelAr: "وكيل تصميم المنظمة", internalOnly: true, reason: "Agent landing page." },
  { id: "agent-policies-grading", route: "/policies/grading-agent", labelEn: "Grading Agent", labelAr: "وكيل التصنيف", internalOnly: true, reason: "Agent landing page." },
  { id: "agent-reports", route: "/reports/agent", labelEn: "Reports Agent", labelAr: "وكيل التقارير", internalOnly: true, reason: "Agent landing page." },
  { id: "agent-settings-le", route: "/settings/legal-entity-agent", labelEn: "Legal Entity Agent", labelAr: "وكيل الكيان القانوني", internalOnly: true, reason: "Agent landing page." },
  { id: "agent-operations-benefits", route: "/operations/benefits-agent", labelEn: "Benefits Agent", labelAr: "وكيل المزايا", internalOnly: true, reason: "Agent landing page." },
  { id: "agent-operations-leaves", route: "/operations/leaves-agent", labelEn: "Leaves Agent", labelAr: "وكيل الإجازات", internalOnly: true, reason: "Agent landing page." },
  { id: "agent-operations-loans", route: "/operations/loans-agent", labelEn: "Loans Agent", labelAr: "وكيل القروض", internalOnly: true, reason: "Agent landing page." },
  { id: "agent-operations-payroll", route: "/operations/payroll-agent", labelEn: "Payroll Agent", labelAr: "وكيل الرواتب", internalOnly: true, reason: "Agent landing page." },
  { id: "agent-operations-ta", route: "/operations/ta-agent", labelEn: "T&A Agent", labelAr: "وكيل الحضور والانصراف", internalOnly: true, reason: "Agent landing page." },
  { id: "agent-operations-tickets", route: "/operations/tickets-agent", labelEn: "Travel Tickets Agent", labelAr: "وكيل تذاكر السفر", internalOnly: true, reason: "Agent landing page." },
  { id: "agent-operations-travel", route: "/operations/travel-agent", labelEn: "Travel Agent", labelAr: "وكيل السفر", internalOnly: true, reason: "Agent landing page." },

  // ---- Detail pages (reached via parent-list row click) ----
  { id: "detail-contract-legacy", route: "/contracts/:contractId", labelEn: "Contract (legacy)", labelAr: "العقد (قديم)", internalOnly: true, reason: "Legacy redirect kept for inbound bookmarks; routes to /people/contracts/:contractId." },
  { id: "detail-comp-change", route: "/operations/comp-changes/:id", labelEn: "Comp Change Detail", labelAr: "تفاصيل تغيير الراتب", internalOnly: true, reason: "Detail page — reached via /operations/comp-changes list row click." },
  { id: "detail-comp-cycle", route: "/operations/comp-cycles/:id", labelEn: "Comp Cycle Detail", labelAr: "تفاصيل دورة الرواتب", internalOnly: true, reason: "Detail page — reached via /operations/comp-cycles list row click." },
  { id: "detail-employee-comp-history", route: "/operations/employees/:id/comp-history", labelEn: "Employee Comp History", labelAr: "تاريخ راتب الموظف", internalOnly: true, reason: "Detail sub-page reached via employee master file." },
  { id: "detail-eos", route: "/operations/eos/:terminationId", labelEn: "EOS Detail", labelAr: "تفاصيل نهاية الخدمة", internalOnly: true, reason: "Detail page — reached via /operations/eos list row click." },
  { id: "detail-leave", route: "/operations/leaves/:leave_id", labelEn: "Leave Detail", labelAr: "تفاصيل الإجازة", internalOnly: true, reason: "Detail page — reached via /operations/leave/requests list row click." },
  { id: "detail-loan", route: "/operations/loans/:loan_id", labelEn: "Loan Detail", labelAr: "تفاصيل القرض", internalOnly: true, reason: "Detail page — reached via /operations/loans/applications list row click." },
  { id: "detail-contract", route: "/people/contracts/:contractId", labelEn: "Contract Detail", labelAr: "تفاصيل العقد", internalOnly: true, reason: "Detail page — reached via /people/contracts list row click." },
  { id: "detail-employee", route: "/people/employees/:employeeId", labelEn: "Employee Master File", labelAr: "ملف الموظف", internalOnly: true, reason: "Detail page — reached via /people/employees list row click." },
  { id: "detail-employee-legacy", route: "/people/employees/:employeeId/legacy", labelEn: "Employee (legacy)", labelAr: "الموظف (قديم)", internalOnly: true, reason: "Legacy detail view kept for inbound links." },
  { id: "detail-employee-comp-history-people", route: "/people/employees/:id/comp-history", labelEn: "Employee Comp History", labelAr: "تاريخ راتب الموظف", internalOnly: true, reason: "Detail sub-page reached via employee master file." },
  { id: "detail-job", route: "/people/jobs/:jobId", labelEn: "Job Detail", labelAr: "تفاصيل الوظيفة", internalOnly: true, reason: "Detail page — reached via /people/jobs list row click." },
  { id: "detail-position", route: "/people/positions/:positionId", labelEn: "Position Detail", labelAr: "تفاصيل المنصب", internalOnly: true, reason: "Detail page — reached via /people/positions list row click." },
  { id: "detail-policy-ai-ingestion-draft", route: "/policies/ai-ingestion/drafts/:draftId", labelEn: "Policy Draft", labelAr: "مسودة السياسة", internalOnly: true, reason: "Detail page — reached via /policies/ai-ingestion drafts list row click." },
  { id: "detail-allowance", route: "/policies/allowances/:allowanceId", labelEn: "Allowance Detail", labelAr: "تفاصيل البدل", internalOnly: true, reason: "Detail page." },
  { id: "detail-approval-chain", route: "/policies/approval-chains/:workflowType", labelEn: "Approval Chain Detail", labelAr: "تفاصيل سلسلة الموافقات", internalOnly: true, reason: "Detail page." },
  { id: "detail-excuse-type", route: "/policies/excuse-types/:excuseTypeId", labelEn: "Excuse Type Detail", labelAr: "تفاصيل نوع العذر", internalOnly: true, reason: "Detail page." },
  { id: "detail-grade", route: "/policies/grades/:gradeId", labelEn: "Grade Detail", labelAr: "تفاصيل الدرجة", internalOnly: true, reason: "Detail page." },
  { id: "detail-leave-type", route: "/policies/leave-types/:leaveTypeId", labelEn: "Leave Type Detail", labelAr: "تفاصيل نوع الإجازة", internalOnly: true, reason: "Detail page." },
  { id: "detail-loan-product", route: "/policies/loan-products/:loanProductId", labelEn: "Loan Product Detail", labelAr: "تفاصيل منتج القرض", internalOnly: true, reason: "Detail page." },
  { id: "detail-shift", route: "/policies/shifts/:shiftId", labelEn: "Shift Detail", labelAr: "تفاصيل النوبة", internalOnly: true, reason: "Detail page." },
  { id: "detail-report-browse", route: "/reports/browse/:axis", labelEn: "Reports Library by Axis", labelAr: "مكتبة التقارير حسب المحور", internalOnly: true, reason: "Detail sub-page — reached via /reports/library navigation." },
  { id: "detail-report-library", route: "/reports/library/:domain", labelEn: "Reports Library by Domain", labelAr: "مكتبة التقارير حسب المجال", internalOnly: true, reason: "Detail sub-page." },
  { id: "detail-user", route: "/settings/users/:user_id", labelEn: "User Detail", labelAr: "تفاصيل المستخدم", internalOnly: true, reason: "Detail page — reached via /settings/users list row click." },

  // ---- Onboarding journey step pages (reached via Settings → Setup) ----
  { id: "onboarding-welcome", route: "/onboarding/welcome", labelEn: "Onboarding Step 1 — Welcome", labelAr: "خطوة الانضمام 1 — الترحيب", internalOnly: true, reason: "Onboarding journey step — reached from /settings/setup landing." },
  { id: "onboarding-company", route: "/onboarding/company", labelEn: "Onboarding Step 2 — Company", labelAr: "خطوة الانضمام 2 — الشركة", internalOnly: true, reason: "Onboarding journey step." },
  { id: "onboarding-policies", route: "/onboarding/policies", labelEn: "Onboarding Step 3 — Policies", labelAr: "خطوة الانضمام 3 — السياسات", internalOnly: true, reason: "Onboarding journey step." },
  { id: "onboarding-jobs-grades", route: "/onboarding/jobs-grades", labelEn: "Onboarding Step 4 — Jobs & Grades", labelAr: "خطوة الانضمام 4 — الوظائف والدرجات", internalOnly: true, reason: "Onboarding journey step." },
  { id: "onboarding-employees", route: "/onboarding/employees", labelEn: "Onboarding Step 5 — Employees", labelAr: "خطوة الانضمام 5 — الموظفون", internalOnly: true, reason: "Onboarding journey step." },
  { id: "onboarding-approvals", route: "/onboarding/approvals", labelEn: "Onboarding Step 6 — Approvals", labelAr: "خطوة الانضمام 6 — الموافقات", internalOnly: true, reason: "Onboarding journey step." },
  { id: "onboarding-integrations", route: "/onboarding/integrations", labelEn: "Onboarding Step 7 — Integrations", labelAr: "خطوة الانضمام 7 — التكاملات", internalOnly: true, reason: "Onboarding journey step." },
  { id: "onboarding-shadow-payroll", route: "/onboarding/shadow-payroll", labelEn: "Onboarding Step 8 — Shadow Payroll", labelAr: "خطوة الانضمام 8 — الرواتب الموازية", internalOnly: true, reason: "Onboarding journey step." },
  { id: "onboarding-go-live", route: "/onboarding/go-live", labelEn: "Onboarding Step 9 — Go Live", labelAr: "خطوة الانضمام 9 — التشغيل المباشر", internalOnly: true, reason: "Onboarding journey step." },
  { id: "settings-setup", route: "/settings/setup", labelEn: "Setup", labelAr: "الإعداد", internalOnly: true, reason: "Onboarding journey re-mount inside Settings module." },

  // ---- M30 single-transaction wizard routes (reached from operations queues) ----
  { id: "transaction-attendance-new", route: "/operations/transactions/attendance/new", labelEn: "New Attendance Transaction", labelAr: "معاملة حضور جديدة", internalOnly: true, reason: "M30 single-transaction wizard — reached from /operations/attendance/exception-queue." },
  { id: "transaction-contract-amendment-new", route: "/operations/transactions/contract-amendment/new", labelEn: "New Contract Amendment", labelAr: "تعديل عقد جديد", internalOnly: true, reason: "M30 single-transaction wizard — reached from /people/contracts." },
  { id: "transaction-eos-new", route: "/operations/transactions/eos/new", labelEn: "Initiate Termination", labelAr: "بدء إنهاء الخدمة", internalOnly: true, reason: "M30 single-transaction wizard — reached from /operations/eos." },
  { id: "transaction-excuse-new", route: "/operations/transactions/excuse/new", labelEn: "New Excuse", labelAr: "عذر جديد", internalOnly: true, reason: "M30 single-transaction wizard — reached from /operations/excuses/queue." },
  { id: "transaction-leave-new", route: "/operations/transactions/leave/new", labelEn: "New Leave on Behalf", labelAr: "إجازة جديدة بالنيابة", internalOnly: true, reason: "M30 single-transaction wizard — reached from /operations/leave/approvals." },
  { id: "transaction-loan-new", route: "/operations/transactions/loan/new", labelEn: "New Loan on Behalf", labelAr: "قرض جديد بالنيابة", internalOnly: true, reason: "M30 single-transaction wizard — reached from /operations/loans/applications." },
  { id: "transaction-overtime-new", route: "/operations/transactions/overtime/new", labelEn: "New OT Entry on Behalf", labelAr: "إدخال ساعات إضافية بالنيابة", internalOnly: true, reason: "M30 single-transaction wizard — reached from /operations/attendance/overtime-approvals." },

  // ---- /me sub-domain landings (reached via /me Rail 2) ----
  { id: "me-attendance-landing", route: "/me/attendance", labelEn: "My Attendance", labelAr: "حضوري", internalOnly: true, reason: "/me Rail 2 sub-domain landing." },
  { id: "me-end-of-service", route: "/me/end-of-service", labelEn: "My End-of-Service", labelAr: "نهاية خدمتي", internalOnly: true, reason: "Legacy /me sub-domain landing — /me/eos is the canonical Session 3d route." },
  { id: "me-leaves-landing", route: "/me/leaves", labelEn: "My Leaves", labelAr: "إجازاتي", internalOnly: true, reason: "/me Rail 2 sub-domain landing." },
  { id: "me-leaves-new", route: "/me/leaves/new", labelEn: "Request Leave", labelAr: "طلب إجازة", internalOnly: true, reason: "Legacy /me sub-route — Session 3d's /me/leaves/request is canonical." },
  { id: "me-loans-landing", route: "/me/loans", labelEn: "My Loans", labelAr: "قروضي", internalOnly: true, reason: "/me Rail 2 sub-domain landing." },
  { id: "me-loans-new", route: "/me/loans/new", labelEn: "Request Loan", labelAr: "طلب قرض", internalOnly: true, reason: "Legacy /me sub-route — Session 3d's /me/loans/apply is canonical." },
  { id: "me-payment-hub", route: "/me/payment-hub", labelEn: "My Payment Hub", labelAr: "مركز مدفوعاتي", internalOnly: true, reason: "Legacy /me payment landing — Session 3d's /me/payments/* is canonical." },
  { id: "me-travel-landing", route: "/me/travel", labelEn: "My Travel", labelAr: "سفري", internalOnly: true, reason: "/me Rail 2 sub-domain landing." },
  { id: "me-travel-new", route: "/me/travel/new", labelEn: "Request Travel", labelAr: "طلب سفر", internalOnly: true, reason: "Legacy /me sub-route — Session 3d's /me/travel/request is canonical." },

  // ---- Settings (admin-style sub-pages with their own form surfaces) ----
  { id: "settings-branding", route: "/settings/branding", labelEn: "Branding", labelAr: "العلامة التجارية", internalOnly: true, reason: "Settings sub-page — single-instance config form." },
  // settings-integrations was previously internalOnly to keep it off the menu.
  // After the M3X.NAV-CLEANUP it's promoted into the Platform & Integrations
  // sub-domain at id="integrations". Drop the duplicate stub so the route
  // appears once and only once in the registry.
  { id: "settings-users", route: "/settings/users", labelEn: "Users & Roles", labelAr: "المستخدمون والأدوار", internalOnly: true, reason: "Settings sub-page — Clerk-driven user management." },

  // ---- EOS specialized wizards reached from /operations/eos detail ----
  { id: "eos-article-80-classify", route: "/operations/eos/article-80-classify", labelEn: "Article 80 Classifier", labelAr: "مصنف المادة 80", internalOnly: true, reason: "Article 80 classifier wizard — reached from EOS detail page." },
  { id: "eos-retirement", route: "/operations/eos/retirement", labelEn: "Retirement Scanner", labelAr: "ماسح التقاعد", internalOnly: true, reason: "Retirement scanner — reached from EOS list." },
  { id: "eos-absconded-checklist", route: "/operations/eos/absconded-checklist", labelEn: "Absconded Checklist", labelAr: "قائمة فحص الهروب", internalOnly: true, reason: "Article 80 absconded 5-condition checklist wizard." },
  { id: "eos-deceased-workflow", route: "/operations/eos/deceased-workflow", labelEn: "Deceased Workflow", labelAr: "سير عمل المتوفى", internalOnly: true, reason: "Deceased employee workflow wizard." },
  { id: "eos-dispute", route: "/operations/eos-dispute", labelEn: "EOS Dispute", labelAr: "نزاع نهاية الخدمة", internalOnly: true, reason: "Dispute resolution surface — reached from EOS detail page." },

  // ---- Other internal/agent operational surfaces ----
  { id: "operations-attendance-ot-calculator", route: "/operations/attendance/ot-calculator", labelEn: "Article 107 OT Calculator", labelAr: "حاسبة الساعات الإضافية المادة 107", internalOnly: true, reason: "Single-use tool — reached from attendance detail pages." },
  { id: "operations-payroll-candidates", route: "/operations/payroll-candidates", labelEn: "Payroll Candidates", labelAr: "مرشحو الرواتب", internalOnly: true, reason: "Continuous accumulator candidate list (M3X.PAYROLL Rule 5)." },
  { id: "operations-loans-schedules", route: "/operations/loans/schedules", labelEn: "Loan Schedules (legacy)", labelAr: "جداول القروض (قديم)", internalOnly: true, reason: "Legacy view — CRUD lives on /operations/loans/active-register." },
  { id: "ai-feedback", route: "/ai/feedback", labelEn: "AI Feedback", labelAr: "ملاحظات الذكاء الاصطناعي", internalOnly: true, reason: "AI feedback inbox (review-only)." },
  { id: "ai-proposals", route: "/ai/proposals", labelEn: "Agent Proposals", labelAr: "اقتراحات الوكلاء", internalOnly: true, reason: "Agent proposals review surface." },

  // ---- Policies aux surfaces (reached from parent policy pages) ----
  { id: "policies-commission-structures", route: "/policies/commission-structures", labelEn: "Commission Structures", labelAr: "هياكل العمولات", internalOnly: true, reason: "Policy sub-page reached from /operations/commission-structures." },
  { id: "policies-comp-bands", route: "/policies/comp-bands", labelEn: "Comp Bands", labelAr: "نطاقات التعويض", internalOnly: true, reason: "Policy sub-page." },
  { id: "policies-compliance-rules", route: "/policies/compliance-rules", labelEn: "Compliance Rules", labelAr: "قواعد الامتثال", internalOnly: true, reason: "Policy sub-page." },
  { id: "policies-eos-rate-matrix", route: "/policies/eos-rate-matrix", labelEn: "EOS Rate Matrix", labelAr: "مصفوفة معدلات نهاية الخدمة", internalOnly: true, reason: "ONB_FLUSH content — view-only." },
  { id: "policies-eos-rate-matrix-autoflush", route: "/policies/eos-rate-matrix/autoflush", labelEn: "EOS Auto-Flush Wizard", labelAr: "معالج التدفق التلقائي لنهاية الخدمة", internalOnly: true, reason: "Auto-flush wizard." },
  { id: "policies-government-layer", route: "/policies/government-layer", labelEn: "Government Layer", labelAr: "الطبقة الحكومية", internalOnly: true, reason: "Government-rule reference (read-only)." },
  { id: "policies-shifts-roster", route: "/policies/shifts/roster", labelEn: "Shifts Roster Planning", labelAr: "تخطيط جدول النوبات", internalOnly: true, reason: "Roster planning visual (M3X.ATTENDANCE F.4) — reached from /policies/shifts." },
  { id: "policies-shifts-rotations", route: "/policies/shifts/rotations", labelEn: "Shift Rotation Patterns", labelAr: "أنماط دوران النوبات", internalOnly: true, reason: "Rotation patterns — reached from /policies/shifts." },

  // ---- Integrations sub-pages ----
  { id: "integrations-biometric", route: "/integrations/biometric", labelEn: "Biometric Machines", labelAr: "آلات البصمة", internalOnly: true, reason: "Integrations sub-page — per-device list reached from /settings/integrations." },

  // ---- Reports sub-pages ----
  { id: "reports-custom", route: "/reports/custom", labelEn: "Custom Reports", labelAr: "التقارير المخصصة", internalOnly: true, reason: "Reports sub-page — alias for /reports/custom-builder." },

  // ---- Legacy redirect routes (Navigate components, kept for inbound bookmarks) ----
  { id: "legacy-employees", route: "/employees", labelEn: "Employees (legacy)", labelAr: "الموظفون (قديم)", internalOnly: true, reason: "Legacy redirect → /people/employees. Kept for inbound bookmarks." },
  { id: "legacy-employees-new", route: "/employees/new", labelEn: "New Employee (legacy)", labelAr: "موظف جديد (قديم)", internalOnly: true, reason: "Legacy redirect → /people/employees/new." },
  { id: "legacy-employees-detail", route: "/employees/:employeeId", labelEn: "Employee (legacy)", labelAr: "موظف (قديم)", internalOnly: true, reason: "Legacy redirect → /people/employees/:employeeId." },
  { id: "legacy-positions", route: "/positions", labelEn: "Positions (legacy)", labelAr: "المناصب (قديم)", internalOnly: true, reason: "Legacy redirect → /people/positions." },
  { id: "legacy-org-chart", route: "/org-chart", labelEn: "Org Chart (legacy)", labelAr: "الهيكل التنظيمي (قديم)", internalOnly: true, reason: "Legacy redirect → /people/org-chart." },
  { id: "legacy-jobs", route: "/jobs", labelEn: "Jobs (legacy)", labelAr: "الوظائف (قديم)", internalOnly: true, reason: "Legacy redirect → /people/jobs." },
  { id: "legacy-grades", route: "/grades", labelEn: "Grades (legacy)", labelAr: "الدرجات (قديم)", internalOnly: true, reason: "Legacy redirect → /policies/grades." },
  { id: "legacy-grades-detail", route: "/grades/:gradeId", labelEn: "Grade (legacy)", labelAr: "درجة (قديم)", internalOnly: true, reason: "Legacy redirect → /policies/grades/:gradeId." },
  { id: "legacy-allowances", route: "/allowances", labelEn: "Allowances (legacy)", labelAr: "البدلات (قديم)", internalOnly: true, reason: "Legacy redirect → /policies/allowances." },
  { id: "legacy-integrations", route: "/integrations", labelEn: "Integrations (legacy)", labelAr: "التكاملات (قديم)", internalOnly: true, reason: "Legacy redirect → /settings/integrations." },
  { id: "legacy-ai-config-root", route: "/ai-config", labelEn: "AI Config (legacy)", labelAr: "إعدادات الذكاء (قديم)", internalOnly: true, reason: "Legacy redirect → /ai/config." },
  { id: "legacy-ai-config", route: "/ai/config", labelEn: "AI Config (legacy)", labelAr: "إعدادات الذكاء (قديم)", internalOnly: true, reason: "Legacy redirect → /ai/behavior." },
  { id: "legacy-ai-activity", route: "/ai/activity", labelEn: "AI Activity (legacy)", labelAr: "نشاط الذكاء (قديم)", internalOnly: true, reason: "Legacy redirect → /ai/activity-log." },
  { id: "legacy-ai-activity-log", route: "/ai/activity-log", labelEn: "AI Activity Log (legacy)", labelAr: "سجل نشاط الذكاء (قديم)", internalOnly: true, reason: "Legacy redirect → /settings/audit-log?actor=ai." },
  { id: "legacy-ai-ingestion-root", route: "/ai/ingestion", labelEn: "AI Ingestion (legacy)", labelAr: "تغذية الذكاء (قديم)", internalOnly: true, reason: "Legacy redirect → /policies/ai-ingestion." },
  { id: "legacy-ai-ingestion-upload", route: "/ai/ingestion/upload", labelEn: "AI Ingestion Upload (legacy)", labelAr: "رفع تغذية الذكاء (قديم)", internalOnly: true, reason: "Legacy redirect → /policies/ai-ingestion." },
  { id: "legacy-ai-ingestion-draft", route: "/ai/ingestion/drafts/:draftId", labelEn: "AI Ingestion Draft (legacy)", labelAr: "مسودة تغذية الذكاء (قديم)", internalOnly: true, reason: "Legacy redirect → /policies/ai-ingestion/drafts/:draftId." },
  { id: "legacy-leave-types", route: "/leave/types", labelEn: "Leave Types (legacy)", labelAr: "أنواع الإجازات (قديم)", internalOnly: true, reason: "Legacy redirect → /policies/leave-types." },
  { id: "legacy-leave-requests", route: "/leave/requests", labelEn: "Leave Requests (legacy)", labelAr: "طلبات الإجازة (قديم)", internalOnly: true, reason: "Legacy redirect → /operations/leave/requests." },
  { id: "legacy-leave-approvals", route: "/leave/approvals", labelEn: "Leave Approvals (legacy)", labelAr: "موافقات الإجازة (قديم)", internalOnly: true, reason: "Legacy redirect → /operations/leave/approvals." },
  { id: "legacy-leave-calendar", route: "/leave/calendar", labelEn: "Leave Calendar (legacy)", labelAr: "تقويم الإجازات (قديم)", internalOnly: true, reason: "Legacy redirect → /operations/leave/calendar." },
  { id: "legacy-loans-products", route: "/loans/products", labelEn: "Loan Products (legacy)", labelAr: "منتجات القروض (قديم)", internalOnly: true, reason: "Legacy redirect → /policies/loan-products." },
  { id: "legacy-loans-applications", route: "/loans/applications", labelEn: "Loan Applications (legacy)", labelAr: "طلبات القروض (قديم)", internalOnly: true, reason: "Legacy redirect → /operations/loans/applications." },

  // ---- The Add Employee wizard (real page, reached from /people/employees + the Onboarding journey, not via menu) ----
  { id: "people-employees-new", route: "/people/employees/new", labelEn: "Add Employee Wizard", labelAr: "معالج إضافة موظف", internalOnly: true, reason: "Add Employee wizard — reached from /people/employees primary CTA and Onboarding Step 5, not from a menu." },
];



export function wireOrphanRoutes(): string[] {
  return NAVIGATION_REGISTRY.flatMap((sd) =>
    sd.screens.filter((s) => s.status === "wire-orphan").map((s) => s.route),
  );
}


export function newPendingRoutes(): string[] {
  return NAVIGATION_REGISTRY.flatMap((sd) =>
    sd.screens.filter((s) => s.status === "new-pending").map((s) => s.route),
  );
}
